/* eslint-disable */
import { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import { useEnumApi } from "contexts/EnumApiContext";
import useModals from 'components/modals/useModal';
import { modals } from 'components/modals/Modals'; 
import { customFileAxios } from "components/axios/customAxios";
import { getEquipmentCategoriesByQuery, getEquipmentComponentLocationByCategory } from "components/axios/equipmentCategoryAxios";
import { getMaintenanceTasksById } from "components/axios/maintenanceAxios";
import { useTranslation } from "react-i18next";
import { isValidDateTimeFormat } from "components/date_picker/dateUtils";

export const useMaintenanceForm = ({ id, onRefresh, type, handleCloseModal }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { getEnumValueByEnumName } = useEnumApi();
  const { name } = useSelector(state => state.user);
  const { openModal } = useModals();

  const [fileLoading, setFileLoading] = useState(false);
  const [inputFiles, setInputFiles] = useState([]);
  const [inputParts, setInputParts] = useState([]);

  const nextID = useRef(1);
  const nextPartId = useRef(1);
  const regexDateTime = RegExp(/^\d{4}.(0[1-9]|1[012]).(0[1-9]|[12][0-9]|3[01])T(0[0-9]|1[0-9]|2[0-3]):(0[1-9]|[0-5][0-9])$/);
  const today = dayjs().tz(process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DDTHH:mm:ssZ');

  const handleAlertClick = (alertMessage) => {
    openModal(modals.alertModal, {
      onClose: () => {},
      message: alertMessage,
    });
  };
// console.log(id)

  const { control, register, handleSubmit, reset, setValue, formState: { isDirty, errors } } = useForm({
    defaultValues: { inputParts: [] }
  });

  // 🚀 데이터 조회 (Maintenance Task)
  const { data: maintenanceTaskData } = useQuery({
    queryKey: ['maintenanceTask-modify', id],
    queryFn: async () => {
        if (!id) return null;
        const maintenanceTask = await getMaintenanceTasksById(id);
        return { maintenanceTask };
    },
    enabled: !!id,
  });
  const maintenanceTask = maintenanceTaskData?.maintenanceTask;
// console.log(maintenanceTask)

  const selectedEquipment = useMemo(() => {
    if (!maintenanceTask?.equipment) return null;
    return {
      "equipmentId": maintenanceTask?.equipment?.id,
      "equipmentName": maintenanceTask?.equipment?.name,
      "equipmentNo": maintenanceTask?.equipment?.equipmentNo,
      "stationName": maintenanceTask?.equipment?.station?.name,
      "subwayLine": maintenanceTask?.equipment?.station?.workingPart.subwayLine,
      "categoryId" : maintenanceTask?.equipment?.equipmentCtgr?.id
    }
  }, [maintenanceTask]);
// console.log(selectedEquipment)
  const category3Id = selectedEquipment?.categoryId;

  // 🚀 설비구성부, 구성부위치 데이터 조회
  const { data: componentData } = useQuery({
    queryKey: ['componentDatas', category3Id],
    queryFn: async () => {
        if (!category3Id) return null;
        const param = { "parent.id": category3Id, depth: 4, size : 100, sort : "name,asc" };
        const [componentsResult, componentLocationsResult] = await Promise.all([
            getEquipmentCategoriesByQuery(param),
            getEquipmentComponentLocationByCategory(category3Id)
        ]);
        return {
            components : componentsResult,
            locations: componentLocationsResult
        };
    },
    enabled: !!category3Id,
  });

  const componentOptions = componentData?.components?._embedded?.equipmentCategories || [];
  const locationOptions = componentData?.locations || [];

  // 파일 핸들러
  function addInputFile() {
    const input = { id: nextID.current, file: {}, rFile: null };
    setInputFiles([...inputFiles, input]);
    nextID.current += 1;
  }

  function deleteInputFile(indexId) {
    setInputFiles(inputFiles.filter(item => item.id !== indexId));
  }

  function handleInputFileChange(e, index) {
    setInputFiles(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, file: e.target.files[0], rFile: null } : item
      )
    );
  }

  // 부품 핸들러
  function addInputPart() {
    const input = {
      id: nextPartId.current,
      part: { id: 0, componentId: "", componentLocationId: "", masterPartId: "", partId: "", partName: "", subPart: "", partLifeSpanTime: "" }
    };
    setInputParts(prev => [...prev, input]);
    nextPartId.current += 1;
  }

  function deleteInputPart(partId) {
    setInputParts(prev => prev.filter(item => item.id !== partId));
  }

  function handlePartChange(partId, key, value) {
    setInputParts(prev =>
      prev.map(item => {
        if (item.id === partId) {
          let updatedPart = { ...item.part, [key]: value };
          if (key === "componentId") {
            updatedPart = { ...updatedPart, partId: "" };
          }
          return { ...item, part: updatedPart };
        }
        return item;
      })
    );
  }

  // 🚀 백엔드 데이터 동기화
  useEffect(() => {
    if (maintenanceTask) {
      if (maintenanceTask?.equipmentParts?.length > 0) {
        const mappedParts = maintenanceTask?.equipmentParts?.map((serverItem, index) => ({
          id: index + 1,
          part: {
            id: serverItem.id,
            componentId: String(serverItem?.masterEquipmentPart?.equipmentCtgr?.id) || "",
            componentLocationId: String(serverItem.componentLocation.id) || "",
            masterPartId: String(serverItem?.masterEquipmentPart?.id) || "",
            partId: String(serverItem.id) || "",
            partName: serverItem.masterEquipmentPart?.name || "",
            subPart: serverItem.subPart || "",
            partLifeSpanTime: serverItem.lifeSpanTime || ""
          }
        }));

        setInputParts(mappedParts);
        nextPartId.current = mappedParts.length + 1;

        setValue("inputParts", mappedParts);
      }

      if (maintenanceTask?.statusReport?.reportFiles?.length > 0) {
        // 원본 코드의 오타 수정 (task -> maintenanceTask)
        const mappedFiles = maintenanceTask?.statusReport?.reportFiles?.map((serverItem, index2) => ({
          id: index2 + 1,
          file: {},
          rFile: maintenanceTask.statusReport.reportFiles[index2]
        })) || [];

        setInputFiles(mappedFiles);
        nextID.current = mappedFiles.length + 1;

        setValue("inputFiles", mappedFiles);
      }

      reset({
        trouble: maintenanceTask.trouble,
        importantYn : maintenanceTask.importantYn,
        remark : maintenanceTask.remark,
        type : maintenanceTask.type,
        worker : maintenanceTask.worker,
        workingBgnAt : maintenanceTask.workingBgnAt ? dayjs(maintenanceTask.workingBgnAt).tz(process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DDTHH:mm:ssZ') : today,
        workingEndAt : maintenanceTask.workingEndAt ? dayjs(maintenanceTask.workingEndAt).tz(process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DDTHH:mm:ssZ') : null
      });

      setValue("trouble", maintenanceTask.trouble);
      setValue("type", maintenanceTask.type);
    }
  }, [maintenanceTask, reset]);

  // 🚀 제출 기능
  const onFormSubmit = (data) => {
console.log(data)
// console.log(dayjs(maintenanceTask.equipmentAlert?.alertedAt).tz(process.env.REACT_APP_TIMEZONE).format("YYYY-MM-DD HH:mm"))
    setFileLoading(true);
    
    if (data.workingBgnAt != null && data.workingBgnAt != '' && !isValidDateTimeFormat(data.workingBgnAt)) {
      handleAlertClick(t('message.common.alert.work.start.datetime.input'));
      setFileLoading(false);
      return;
    }
    if (data.workingEndAt != null && data.workingEndAt != '' && !isValidDateTimeFormat(data.workingEndAt)) {
      handleAlertClick(t('message.common.alert.work.end.datetime.input'));
      setFileLoading(false);
      return;
    }
    
    if (maintenanceTask.equipmentAlert?.alertedAt > data.workingBgnAt) {
      handleAlertClick(t('message.common.alert.work.start.datetime.invalid')+`(${dayjs(maintenanceTask.equipmentAlert?.alertedAt).tz(process.env.REACT_APP_TIMEZONE).format("YYYY-MM-DD HH:mm")})`);
      setFileLoading(false);
      return;
    }

    if (data.workingEndAt !== null && data.workingEndAt != '') {
      if (data.workingBgnAt > data.workingEndAt) {
        handleAlertClick(t('message.common.alert.work.end.datetime.not.equal.work.begin.datetime'));
        setFileLoading(false);
        return;
      }
    }

    if (!inputParts || inputParts.length === 0) {
      handleAlertClick(t('message.common.alert.at.least.one.input.detail.required'));
      setFileLoading(false);
      return;
    }

    const isAllValid = inputParts.every(item => item.part.componentId && item.part.componentLocationId && item.part.partId);
    if (!isAllValid) {
      const invalidIndex = inputParts.findIndex(item => !item.part.componentId || !item.part.componentLocationId || !item.part.partId);
      if (invalidIndex !== -1) {
        handleAlertClick(t('message.common.alert.missing.required.input.fields',{ num: (invalidIndex + 1) }));
        setFileLoading(false);
        return;
      }
    }

    let dto = {
      id : maintenanceTask?.id,
      equipment : {id: selectedEquipment?.equipmentId},
      cost : 0,
      importantYn : data.importantYn,
      remark : data.remark,
      trouble : data.trouble,
      type : data.type,
      worker : data.worker,
      // workingBgnAt : dayjs(data.workingBgnAt).format("YYYY-MM-DDTHH:mm:ssZ"),
      // workingEndAt : data.workingEndAt ? dayjs(data.workingEndAt).format("YYYY-MM-DDTHH:mm:ssZ") : null,
      workingBgnAt : data.workingBgnAt,
      workingEndAt : data.workingEndAt,
    };

    if(maintenanceTask?.status === 'O3_C') {
      dto.type = maintenanceTask?.type;
    }

    if (maintenanceTask?.status !== 'O3_C')
      dto.status = !data.workingEndAt ? 'O2_P' : 'O3_C';
    else
      dto.status = maintenanceTask?.status;

    dto.statusReport = {};
    if (maintenanceTask?.statusReport?.id)
        dto.statusReport.id = (maintenanceTask?.statusReport?.id);

    let formData = new FormData();
    if(inputFiles) {
      for (var l=0; l<inputFiles.length; l++) {
          if (inputFiles[l].file && inputFiles[l].file.name) {
              formData.append("statusFiles", inputFiles[l].file);
          }
      }
    }

    if(maintenanceTask?.status !== 'O3_C') { 
      let equipmentParts = [];
      let isDuple = false;
      if(inputParts && inputParts.length > 0) {
        for (var m=0; m<inputParts.length; m++) {
            if (inputParts[m].part.partId) {
              let dupleChk = inputParts.filter(item => String(item.part.masterPartId) === String(inputParts[m].part.masterPartId));
              if (dupleChk.length > 1) {
                isDuple = true;
                break;
              } else {
                if (inputParts[m].part.id !== 0) {
                  equipmentParts.push({ id: inputParts[m].part.id, subPart : inputParts[m].part.subPart });
                } else {
                  equipmentParts.push({
                    lifeSpanTime: inputParts[m].part.partLifeSpanTime, 
                    masterEquipmentPart: { id: inputParts[m].part.partId }, 
                    componentLocation : { id : inputParts[m].part.componentLocationId },
                    subPart : inputParts[m].part.subPart
                  });
                }
              }
            }
        }
        dto.equipmentParts = equipmentParts;
      }
      
      if (isDuple) {
        handleAlertClick(t('message.common.alert.duplication.material.check.again'));
        setFileLoading(false);
        return;
      }
      if (equipmentParts.length === 0) {
        handleAlertClick(t('message.common.alert.material.select.add'));
        setFileLoading(false);
        return;
      }
    }

    formData.append("dto", new Blob([JSON.stringify(dto)], {type: "application/json"}));
console.log(dto)
    openModal(modals.confirmModal, {
      onSubmit : () => {
        try {
            customFileAxios.patch(`/api/maintenanceTasks/${maintenanceTask.id}`, formData).then((res)=>{
            handleAlertClick(t('message.common.alert.modify.done'));
            setFileLoading(false);
            queryClient.invalidateQueries({ queryKey: ['maintenanceTask-modify', maintenanceTask.id] });
            if (onRefresh) onRefresh();
            reset();
            nextID.current = 1;
            setInputFiles([]);
            nextPartId.current = 1;
            setInputParts([]);
            if (handleCloseModal) handleCloseModal(); // 모달 닫기 콜백 실행
        }).catch((err)=>{
            setFileLoading(false);
            handleAlertClick(err.response?.data?.message || err.message);
        });
        } catch (error) {
            console.error("error:", error);
            throw error;
        }
      },
      onClose : () => { setFileLoading(false); },
      message: t('message.common.alert.modify.question'),
    });
  };

  return {
    control, register, handleSubmit, errors, isDirty, name,
    fileLoading, inputFiles, inputParts, componentOptions, locationOptions, maintenanceTask, type,
    getEnumValueByEnumName, addInputFile, deleteInputFile, handleInputFileChange,
    addInputPart, deleteInputPart, handlePartChange, onFormSubmit,

    setEndAtNow: () => {
      // 버튼 누른 시점의 현재 날짜/시간을 입력 포맷에 맞게 생성
      // const nowStr = today;
      // react-hook-form의 상태값을 수동으로 업데이트 (isDirty도 정상 반영됨)
      setValue("workingEndAt", today, { shouldValidate: true, shouldDirty: true });
    }
  };
};